import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ReviewService } from '@/lib/reviewService';
import SpamDetectionService from '@/lib/spamDetectionService';
import { CreateReviewData, ReviewTargetType, CategoryRatings } from '@/types/reviews';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Star, Heart, ThumbsUp, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

interface StarRatingProps {
  rating: number;
  onRatingChange: (rating: number) => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  label?: string;
}

const StarRating: React.FC<StarRatingProps> = ({ 
  rating, 
  onRatingChange, 
  size = 'md', 
  disabled = false,
  label 
}) => {
  const [hoverRating, setHoverRating] = useState(0);

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className="flex flex-col gap-2">
      {label && <label className="text-sm font-medium">{label}</label>}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={disabled}
            className={`${sizeClasses[size]} transition-colors ${disabled ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
            onMouseEnter={() => !disabled && setHoverRating(star)}
            onMouseLeave={() => !disabled && setHoverRating(0)}
            onClick={() => !disabled && onRatingChange(star)}
          >
            <Star
              className={`w-full h-full ${
                star <= (hoverRating || rating)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

interface CategoryRatingsProps {
  targetType: ReviewTargetType;
  ratings: CategoryRatings;
  onRatingsChange: (ratings: CategoryRatings) => void;
  disabled?: boolean;
}

const CategoryRatingsComponent: React.FC<CategoryRatingsProps> = ({
  targetType,
  ratings,
  onRatingsChange,
  disabled = false
}) => {
  const displayCategories = [
    { key: 'holiday_spirit', label: '🎶 Holiday Spirit', description: 'How strong was the holiday spirit, creativity, or storytelling?' },
    { key: 'theming_atmosphere', label: '🎭 Theming & Atmosphere', description: 'Whether spooky or festive, this captures how well the display evoked its intended vibe.' },
    { key: 'drive_by_experience', label: '🚗 Drive-by Experience', description: 'Was it easy to view from your car? Was traffic managed well?' }
  ];

  const sequenceCategories = [
    { key: 'quality', label: 'Sequence Quality', description: 'Programming and timing quality' },
    { key: 'music', label: 'Music Selection', description: 'How well does the music fit?' },
    { key: 'difficulty', label: 'Implementation', description: 'How easy to implement?' },
    { key: 'documentation', label: 'Documentation', description: 'Clear instructions provided?' }
  ];

  const categories = targetType === 'display' ? displayCategories : sequenceCategories;

  const handleRatingChange = (category: string, rating: number) => {
    onRatingsChange({
      ...ratings,
      [category]: rating
    });
  };

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-sm">Category Ratings (Optional)</h4>
      <div className="grid gap-4">
        {categories.map(({ key, label, description }) => (
          <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <div className="font-medium text-sm">{label}</div>
              <div className="text-xs text-gray-600">{description}</div>
            </div>
            <StarRating
              rating={ratings[key] || 0}
              onRatingChange={(rating) => handleRatingChange(key, rating)}
              size="sm"
              disabled={disabled}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

interface ReviewFormProps {
  targetType: ReviewTargetType;
  targetId: string;
  targetName?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  className?: string;
}

export const ReviewForm: React.FC<ReviewFormProps> = ({
  targetType,
  targetId,
  targetName,
  onSuccess,
  onCancel,
  className = ''
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canReview, setCanReview] = useState(true);
  const [hasVerifiedPurchase, setHasVerifiedPurchase] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Form state
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [recommended, setRecommended] = useState<boolean | undefined>(undefined);
  const [categoryRatings, setCategoryRatings] = useState<CategoryRatings>({});

  // Check if user can review and if they have verified purchase
  useEffect(() => {
    const checkEligibility = async () => {
      if (!user) return;

      try {
        const canUserReview = await ReviewService.canUserReview(targetType, targetId, user.id);
        setCanReview(canUserReview);

        if (targetType === 'sequence') {
          const hasPurchased = await ReviewService.hasUserPurchased(targetId, user.id);
          setHasVerifiedPurchase(hasPurchased);
        }
      } catch (error) {
        console.error('Error checking review eligibility:', error);
    }
  };

    checkEligibility();
  }, [user, targetType, targetId]);

  const validateForm = () => {
    const errors: string[] = [];

    if (rating === 0) {
      errors.push('Please provide a star rating');
    }

    if (title && title.length > 200) {
      errors.push('Review title must be 200 characters or less');
    }

    if (content && content.length > 2000) {
      errors.push('Review content must be 2000 characters or less');
    }

    if (content && content.length < 10 && content.length > 0) {
      errors.push('Review content must be at least 10 characters if provided');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || isSubmitting) return;

    setIsSubmitting(true);
    setErrors([]);

    try {
      // Basic validation
      const validation = validateForm();
      if (!validation.isValid) {
        setErrors(validation.errors);
        return;
      }

      // Spam detection check
      const spamResult = await SpamDetectionService.detectSpam(
        content,
        title,
        user.id,
        targetType,
        targetId
      );

      // Log spam detection result
      await SpamDetectionService.logSpamDetection(
        user.id,
        targetType,
        targetId,
        spamResult,
        spamResult.recommendedAction === 'block' ? 'blocked' : 
        spamResult.recommendedAction === 'flag' ? 'flagged' : 'allowed'
      );

      // Handle spam detection results
      if (spamResult.recommendedAction === 'block') {
        setErrors([
          'Your review could not be submitted due to content policy violations.',
          ...spamResult.reasons.slice(0, 3) // Show first 3 reasons
        ]);
        toast.error('Review blocked due to policy violations');
      return;
    }

      // Prepare review data
      const reviewData: CreateReviewData = {
        target_type: targetType,
        target_id: targetId,
        rating,
        title: title.trim() || undefined,
        content: content.trim() || undefined,
        recommended,
        category_ratings: Object.keys(categoryRatings).length > 0 ? categoryRatings : undefined,
        // Set moderation status based on spam detection
        moderation_status: spamResult.recommendedAction === 'flag' ? 'pending' : undefined
      };

      // Submit review
      const newReview = await ReviewService.createReview(reviewData);
      
      // Show appropriate success message based on moderation status
      if (spamResult.recommendedAction === 'flag') {
        toast.success('Review submitted and is pending moderation review.');
      } else {
        toast.success('Review submitted successfully!');
      }

      // Show warning if flagged but allowed
      if (spamResult.isSpam && spamResult.riskLevel === 'medium') {
        toast.warning('Your review has been flagged for review but is visible.');
      }
      
      // Reset form
      setRating(0);
      setTitle('');
      setContent('');
      setRecommended(undefined);
      setCategoryRatings({});
      
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting review:', error);
      
      // Check if it's a rate limiting error
      if (error instanceof Error && error.message.includes('rate limit')) {
        setErrors(['You are submitting reviews too quickly. Please wait before submitting another review.']);
        toast.error('Rate limit exceeded. Please wait before submitting another review.');
      } else {
        toast.error('Failed to submit review. Please try again.');
    }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <Alert>
            <AlertDescription>
              Please sign in to write a review.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!canReview) {
    return (
      <Card className={className}>
        <CardContent className="pt-6">
          <Alert>
            <AlertDescription>
              You have already reviewed this {targetType}.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5" />
          Write a Review
          {targetName && <span className="text-sm font-normal text-gray-600">for {targetName}</span>}
        </CardTitle>
        {targetType === 'sequence' && hasVerifiedPurchase && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle className="w-4 h-4" />
            Verified Purchase
          </div>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Errors */}
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Overall Rating */}
          <div>
            <StarRating
              rating={rating}
              onRatingChange={setRating}
              size="lg"
              label="Overall Rating *"
              disabled={isSubmitting}
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Review Title (Optional)
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Summarize your experience..."
              maxLength={200}
              disabled={isSubmitting}
            />
            <div className="text-xs text-gray-500 mt-1">
              {title.length}/200 characters
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Review Details (Optional)
            </label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={`Tell others about your experience with this ${targetType}...`}
              maxLength={2000}
              rows={4}
              disabled={isSubmitting}
            />
            <div className="text-xs text-gray-500 mt-1">
              {content.length}/2000 characters
            </div>
          </div>

          {/* Recommendation */}
          <div>
            <label className="block text-sm font-medium mb-3">
              Would you recommend this {targetType}?
              </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setRecommended(true)}
                disabled={isSubmitting}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  recommended === true
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ThumbsUp className="w-4 h-4" />
                Yes
              </button>
              <button
                type="button"
                onClick={() => setRecommended(false)}
                disabled={isSubmitting}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  recommended === false
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Heart className="w-4 h-4" />
                No
              </button>
            </div>
          </div>

          {/* Category Ratings */}
          <CategoryRatingsComponent
            targetType={targetType}
            ratings={categoryRatings}
            onRatingsChange={setCategoryRatings}
            disabled={isSubmitting}
          />

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting || rating === 0}
              className="flex-1"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Submit Review
            </Button>
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ReviewForm; 